import { Link, useParams } from "react-router-dom";
import { ROOT, WELCOME } from "@/lib/links";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useMemo } from "react";
import Title from "@/components/Title";
import CommunicationGeneratorDemo from "@/components/newLanding/demos/CommunicationGeneratorDemo";

interface ToolMeta {
  slug: string;
  key: string; // translation key base: spec.{key}.*
}

const TOOL_SPECS: ToolMeta[] = [
  { slug: "lesson-lab", key: "lessonLab" },
  { slug: "communication-generator", key: "communicationGenerator" },
  { slug: "researcher", key: "researcher" },
  { slug: "resource-customiser", key: "resourceCustomiser" },
  { slug: "catchup-coach", key: "catchupCoach" },
];

export default function ToolSpecPage() {
  const { toolSlug } = useParams();
  const { t } = useTranslation(["newLanding"]);
  const meta = useMemo(
    () => TOOL_SPECS.find((tl) => tl.slug === toolSlug),
    [toolSlug]
  );

  if (!meta) {
    return (
      <main className="max-w-3xl px-4 py-20 mx-auto">
        <p className="mb-6 text-sm opacity-80">Unknown tool.</p>
        <Button asChild size="sm" variant="outline">
          <Link to={ROOT}>{t("spec.back")}</Link>
        </Button>
      </main>
    );
  }

  const base = `spec.${meta.key}`;
  const titleText = t(`${base}.title`);

  const demo = (() => {
    switch (toolSlug) {
      case "communication-generator":
        return <CommunicationGeneratorDemo />;
      // case "lesson-lab":
      //   return <LessonLabDemo />;
      // case "researcher":
      //   return <ResearcherDemo />;
      // case "resource-customiser":
      //   return <ResourceCustomiserDemo />;
      default:
        return (
          <div className="rounded-xl border border-dashed border-border min-h-[260px] flex items-center justify-center text-muted-foreground text-sm">
            {t("spec.demo.placeholder")}
          </div>
        );
    }
  })();

  return (
    <main className="flex flex-col w-full min-h-screen px-4 py-10 sm:py-16">
      <div className="flex flex-col w-full max-w-6xl gap-12 mx-auto">
        <div className="flex items-center justify-between gap-4">
          <Title title={meta.key}>{titleText}</Title>
          <Button asChild variant="ghost" size="sm">
            <Link to={WELCOME}>{t("spec.back")}</Link>
          </Button>
        </div>

        {/* Demo first */}
        <section aria-labelledby="demo-heading" className="flex flex-col gap-4">
          <h2
            id="demo-heading"
            className="text-xl font-semibold tracking-tight"
          >
            {t("spec.demo.heading")}
          </h2>
          {demo}
        </section>

        {/* Two-column: explainer left, video right */}
        <section className="grid items-start gap-10 lg:grid-cols-2">
          <div
            className="flex flex-col order-2 gap-8 lg:order-1"
            aria-labelledby="how-heading"
          >
            <div className="flex flex-col gap-4">
              <h2
                id="how-heading"
                className="text-xl font-semibold tracking-tight"
              >
                {t("spec.how.heading")}
              </h2>
              <p className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground">
                {t(`${base}.how`)}
              </p>
            </div>
          </div>
          <div
            className="flex flex-col order-1 gap-4 lg:order-2"
            aria-labelledby="video-heading"
          >
            <h2
              id="video-heading"
              className="text-xl font-semibold tracking-tight"
            >
              {t("spec.video.heading")}
            </h2>
            <AspectRatio
              ratio={16 / 9}
              className="flex items-center justify-center border bg-muted rounded-xl border-border"
            >
              <span className="text-sm text-muted-foreground">
                Video placeholder
              </span>
            </AspectRatio>
          </div>
        </section>
      </div>
    </main>
  );
}
